import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {authService} from '../api';
import { setClientToken, resetClientToken } from '../api/apiClient';

type AuthUser = {
  id: string;
  email: string;
  fullName?: string;
  name?: string;
  role?: 'client' | 'partner' | 'admin';
  type?: 'creator' | 'marketer' | 'agency' | 'admin';
  avatar?: string | null;
  balance?: number;
};

type Notification = {
  id: string;
  message: string;
  created_at: string;
  was_read: boolean;
  alert_message?: string | null;
};

type NotificationsResponse = {
  page: number;
  page_size: number;
  count: number;
  objects: Notification[];
} | null;

type AuthContextValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, role?: 'client' | 'partner' | 'admin') => Promise<void>;
  loginWithToken: (token: string, role?: 'client' | 'partner' | 'admin') => Promise<void>;
  googleAuth: () => Promise<void>;
  googleAuthPartner: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  notifications: NotificationsResponse;
  getNotifications: (page?: number, pageSize?: number) => Promise<void>;
  markNotificationAsRead: (id: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  proposalsLength: number;
  ordersLength: number;
  ordersActiveLength: number;
};

const STORAGE_KEY = 'onlyu-admin-auth-user';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notifications, setNotifications] = useState<NotificationsResponse>(null);
  const [proposalsLength, setProposalsLength] = useState(0);
  const [ordersLength, setOrdersLength] = useState(0);
  const [ordersActiveLength, setOrdersActiveLength] = useState(0);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const rawUser = localStorage.getItem(STORAGE_KEY);
        const authToken = localStorage.getItem('authToken');

        if (rawUser && authToken) {
          const parsed: AuthUser = JSON.parse(rawUser);

          // Проверяем валидность токена
          try {
            setClientToken(authToken);
            const userResponse = await authService.getMe({});

            // Если токен валиден, восстанавливаем пользователя
            if (userResponse?.data) {
              setUser(parsed);
            } else {
              // Токен невалиден, очищаем все
              localStorage.removeItem(STORAGE_KEY);
              localStorage.removeItem('authToken');
              localStorage.removeItem('userRole');
              resetClientToken();
            }
          } catch (error) {
            // Токен невалиден или истек, очищаем все
            console.warn('[AuthContext] Token validation failed', error);
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem('authToken');
            localStorage.removeItem('userRole');
            resetClientToken();
          }
        }
      } catch (error) {
        console.warn('[AuthContext] Failed to restore user from storage', error);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        resetClientToken();
      } finally {
        setIsInitialized(true);
      }
    };

    bootstrap();
  }, []);

  const persistUser = useCallback((authUser: AuthUser | null) => {
    if (!authUser) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authUser));
  }, []);

  const authenticateUser = useCallback((authUser: AuthUser) => {
    setUser(authUser);
    persistUser(authUser);
  }, [persistUser]);

  const login = useCallback(async (email: string, password: string, role: 'client' | 'partner' | 'admin' = 'admin') => {
    setIsProcessing(true);
    try {
      const response = await authService.signIn({ email, password });
      const tokenFromResponse = response?.data?.access_token
        ?? response?.data?.token
        ?? response?.data?.accessToken;

      if (!tokenFromResponse) {
        throw new Error('auth.missingToken');
      }

      const bearerToken = tokenFromResponse.startsWith('Bearer') ? tokenFromResponse : `Bearer ${tokenFromResponse}`;
      localStorage.setItem('authToken', bearerToken);
      localStorage.setItem('userRole', role);
      setClientToken(bearerToken);

      const userResponse = await authService.getMe({});
      const userData = userResponse?.data ?? {};

      const authUser: AuthUser = {
        id: userData.id ?? 'admin',
        email: userData.email ?? email,
        name: userData.name ?? userData.full_name ?? 'OnlyU Admin',
        role,
        type: (userData.type as AuthUser['type']) ?? (role === 'partner' ? 'marketer' : role === 'client' ? 'creator' : 'admin'),
        avatar: userData.avatar ?? null,
        balance: typeof userData.balance === 'number' ? userData.balance : 0,
      };

      authenticateUser(authUser);
    } catch (error) {
      resetClientToken();
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [authenticateUser]);

  const loginWithToken = useCallback(async (token: string, role: 'client' | 'partner' | 'admin' = 'admin') => {
    setIsProcessing(true);
    try {
      const bearerToken = token.startsWith('Bearer') ? token : `Bearer ${token}`;
      localStorage.setItem('authToken', bearerToken);
      localStorage.setItem('userRole', role);
      setClientToken(bearerToken);

      const userResponse = await authService.getMe({});
      const userData = userResponse?.data ?? {};

      const authUser: AuthUser = {
        id: userData.id ?? 'admin-token',
        email: userData.email ?? 'admin@onlyu.dev',
        name: userData.name ?? userData.full_name ?? 'OnlyU Admin',
        role,
        type: (userData.type as AuthUser['type']) ?? (role === 'partner' ? 'marketer' : role === 'client' ? 'creator' : 'admin'),
        avatar: userData.avatar ?? null,
        balance: typeof userData.balance === 'number' ? userData.balance : 0,
      };

      authenticateUser(authUser);
    } catch (error) {
      resetClientToken();
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [authenticateUser]);

  const googleAuth = useCallback(async () => {
    // Placeholder: backend Google auth not yet integrated
    await Promise.resolve();
  }, []);

  const googleAuthPartner = useCallback(async () => {
    // Placeholder: backend Google auth not yet integrated
    await Promise.resolve();
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    persistUser(null);
    setNotifications(null);
    setProposalsLength(0);
    setOrdersLength(0);
    setOrdersActiveLength(0);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    resetClientToken();
  }, [persistUser]);

  const getNotifications = useCallback(async (page = 1, pageSize = 10) => {
    setNotifications({
      page,
      page_size: pageSize,
      count: 0,
      objects: [],
    });
  }, []);

  const markNotificationAsRead = useCallback(async () => {
    // No-op for now; keep signature for compatibility
  }, []);

  const markAllNotificationsAsRead = useCallback(async () => {
    // No-op for now; keep signature for compatibility
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userResponse = await authService.getMe({});
      const userData = userResponse?.data ?? {};

      if (user) {
        const updatedUser: AuthUser = {
          ...user,
          email: userData.email ?? user.email,
          name: userData.name ?? userData.full_name ?? user.name,
          avatar: userData.avatar ?? user.avatar,
          balance: typeof userData.balance === 'number' ? userData.balance : user.balance,
        };

        setUser(updatedUser);
        persistUser(updatedUser);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to refresh user', error);
    }
  }, [user, persistUser]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isAuthenticated: Boolean(user),
    isLoading: !isInitialized || isProcessing,
    login,
    loginWithToken,
    googleAuth,
    googleAuthPartner,
    logout,
    refreshUser,
    notifications,
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    proposalsLength,
    ordersLength,
    ordersActiveLength,
  }), [
    user,
    isInitialized,
    isProcessing,
    login,
    logout,
    loginWithToken,
    googleAuth,
    googleAuthPartner,
    refreshUser,
    notifications,
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    proposalsLength,
    ordersLength,
    ordersActiveLength,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
